import React, { ReactElement, useEffect, useState } from 'react';

import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DesktopDatePicker from '@mui/lab/DesktopDatePicker';

import { useFormContext } from 'react-hook-form';

import { getLabelFallback, onKeyPressOnlyNumber } from '@utils/utils';

import CalendarIcon from '@images/svg-icons/calendar.svg';
import Error from '@images/svg-icons/error.svg';
import Success from '@images/svg-icons/success.svg';

type CalendarInputProps = {
    placeholder: string;
    required?: boolean;
    claim?: string;
    id: string;
    pattern?: {value: any, message: string};
    onChange?: Function;
    disableFuture?: boolean;
    defaultValue: null | string;
    validate?: any;
    mask?: string;
    inputFormat?: string;
    views?: any;
    minDate?: any;
    onChangeInput?: any;
    maxLength?: number;
}

const CalendarInput = ( props : CalendarInputProps ) : ReactElement => {
    const { register, formState: {errors}, setError, clearErrors } = useFormContext();

    const {
        placeholder,
        required,
        claim,
        id,
        pattern,
        onChange,
        disableFuture,
        defaultValue, 
        validate,
        mask,
        inputFormat,
        views,
        minDate,
        onChangeInput,
        maxLength
    } = props;

    const [value, setValue] = useState(defaultValue);
    const [hasError, setHasError] = useState(false);

    const valid = !hasError && !errors[id] && !!value;

    useEffect(() => {
        setValue(defaultValue);
    }, [defaultValue]);

    return(
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DesktopDatePicker
                disableFuture={disableFuture}
                label={placeholder}
                value={value === undefined ? null : value}
                views={views}
                minDate={minDate}
                onChange={(newValue) => {
                    /* @ts-expect-error */
                    const isValid = validate(document.querySelector('#'+id).value);
                    setHasError(!isValid);
                    if(isValid === false) {
                        setError(id,{
                            type: "manual",
                            message: getLabelFallback('INVALID_FIELD_VALUE').replace('{FIELD_NAME}', placeholder.toLocaleLowerCase()),
                          });
                    } else 
                    {
                        clearErrors(id);
                    }
                    setValue(newValue);
                    onChange && onChange(newValue);
                }}
                components={{
                    OpenPickerIcon: () => {return <CalendarIcon/>}
                }}
                onError={(value) => {!!value && value.length > 0 && setHasError(true)}}
                mask={mask}
                inputFormat={inputFormat}
                renderInput={({ inputRef, inputProps, InputProps }) => (
                    <div className={`calendar-input`}>
                        <div className="calendar-input-container">
                            <input id={id} {...register(id, {required: required, pattern: pattern})}
                                type="text" 
                                className={`calendar-input-element ${hasError || !!errors[id] ? 'not-valid' : ''} ${valid ? 'valid' : ''}`} 
                                ref={inputRef} 
                                aria-invalid={errors[id] ? 'true' : 'false'}
                                {...inputProps} 
                                placeholder={placeholder}
                                onKeyPress={(e) => onKeyPressOnlyNumber(e)}
                                onKeyUp={(e) => onChangeInput && onChangeInput(e)}
                                maxLength={maxLength}
                            />
                            <label className="calendar-input-element-label">{placeholder}</label>
                            <div className="calendar-input-element-calendar">
                                <div className="calendar-input-icon">{errors[id] ? <Error /> : valid ? <Success /> : ''}</div>
                                {InputProps?.endAdornment}
                            </div>
                        </div>
                        {!!claim && claim.length > 0 && <span className="calendar-input-element-claim">{claim}</span>}
                        {errors[id] && <span className="input-error">{errors[id].message || getLabelFallback('FIELD_REQUIRED_LABEL').replace('{FIELD_NAME}', placeholder.toLocaleLowerCase())}</span>}
                    </div>
                  )}
            />
        </LocalizationProvider>
    )
}

export default CalendarInput;